// File: frontend/src/contexts/VenueBrandContext.tsx
//
// Applies a partner bar's brand (utils/venues.ts) while one of its pages is
// mounted: the bar page, a bar contest's builder or Scorecard. The CSS
// variables go on <html>, so pop-ups and modals rendered outside the page
// follow; when the last bar page unmounts they are removed and the app's own
// defaults (index.css :root) come back. Nothing leaks onto other pages.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { activeVenueSlug, brandVariables, venueTheme, VenueTheme } from '@/utils/venues';

interface Registration { id: number; slug: string | null }

interface VenueBrandContextValue {
  activeVenue: VenueTheme | null;
  register: (slug: string | null) => number;
  unregister: (id: number) => void;
}

const VenueBrandContext = createContext<VenueBrandContextValue | null>(null);

const loadedFontLinks = new Set<string>();

function loadFonts(theme: VenueTheme) {
  if (typeof document === 'undefined' || loadedFontLinks.has(theme.googleFontsHref)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = theme.googleFontsHref;
  document.head.appendChild(link);
  loadedFontLinks.add(theme.googleFontsHref);
}

export const VenueBrandProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const nextId = useRef(1);

  const register = useCallback((slug: string | null) => {
    const id = nextId.current++;
    setRegistrations(prev => [...prev, { id, slug }]);
    return id;
  }, []);
  const unregister = useCallback((id: number) => {
    setRegistrations(prev => prev.filter(r => r.id !== id));
  }, []);

  const activeSlug = activeVenueSlug(registrations);
  const activeVenue = venueTheme(activeSlug);

  useEffect(() => {
    const root = document.documentElement;
    if (!activeVenue) return;
    loadFonts(activeVenue);
    const variables = brandVariables(activeVenue);
    Object.entries(variables).forEach(([name, value]) => root.style.setProperty(name, value));
    root.dataset.venue = activeVenue.slug;
    return () => {
      Object.keys(variables).forEach(name => root.style.removeProperty(name));
      delete root.dataset.venue;
    };
  }, [activeVenue]);

  const value = useMemo(() => ({ activeVenue, register, unregister }), [activeVenue, register, unregister]);
  return <VenueBrandContext.Provider value={value}>{children}</VenueBrandContext.Provider>;
};

/** Brand the app as this bar while the calling component is mounted (null = no bar). */
export function useVenueBrand(slug: string | null | undefined): void {
  const context = useContext(VenueBrandContext);
  const normalized = venueTheme(slug)?.slug ?? null;
  useEffect(() => {
    if (!context || !normalized) return;
    const id = context.register(normalized);
    return () => context.unregister(id);
    // register/unregister are stable; re-register only when the bar changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalized]);
}

/** The bar currently branding the app, for the header logo. */
export function useActiveVenue(): VenueTheme | null {
  return useContext(VenueBrandContext)?.activeVenue ?? null;
}
